import React, { useState, useEffect, useCallback } from 'react';
import { Brain, Calendar, Users, CheckSquare, AlertCircle, RefreshCw, X } from 'lucide-react';
import { documentService } from '../../services/api';

const DocumentAIInsights = ({ document, onClose, onReprocess }) => {
  const [aiStatus, setAiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(false);
  const [error, setError] = useState('');

  const loadAiStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await documentService.getAiStatus(document.id);
      setAiStatus(response.data);
    } catch (err) {
      setError('Failed to load AI insights');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [document.id]);

  useEffect(() => {
    if (document?.id) {
      loadAiStatus();
    }
  }, [document?.id, loadAiStatus]);

  const handleReprocess = async () => {
    try {
      setReprocessing(true);
      await documentService.processAi(document.id);
      // Wait a moment then reload status
      setTimeout(() => {
        loadAiStatus();
        setReprocessing(false);
      }, 2000);
      if (onReprocess) {
        onReprocess();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(detail || 'Failed to trigger processing');
      setReprocessing(false);
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not processed';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-[9999]">
      {/* Backdrop that doesn't cover navbar */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            <span className="ml-2">Loading AI insights...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]">
      {/* Backdrop that doesn't cover navbar */}
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Brain className="h-6 w-6 text-yellow-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">AI Document Analysis</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleReprocess}
              disabled={reprocessing}
              className="flex items-center px-3 py-2 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${reprocessing ? 'animate-spin' : ''}`} />
              {reprocessing ? 'Processing...' : (aiStatus?.ai_processed ? 'Reprocess' : 'Process with AI')}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Document Info */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Document Details</h4>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700"><strong>File:</strong> {document.original_file_name}</p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>AI Processed:</strong> {formatDate(aiStatus?.ai_processed_at)}
              </p>
              {aiStatus?.ai_processing_error && (
                <p className="text-sm text-red-600 mt-1">
                  <strong>Error:</strong> {aiStatus.ai_processing_error}
                </p>
              )}
            </div>
          </div>

          {aiStatus?.ai_processed && !aiStatus?.ai_processing_error && (
            <>
              {/* Summary */}
              {document.ai_summary && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">AI Summary</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{document.ai_summary}</p>
                  </div>
                </div>
              )}

              {/* Legal Significance */}
              {document.ai_legal_significance && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Legal Significance</h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-gray-700">{document.ai_legal_significance}</p>
                  </div>
                </div>
              )}

              {/* Key Points */}
              {document.ai_key_points && document.ai_key_points.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Key Points</h4>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {document.ai_key_points.map((point, index) => (
                        <li key={index} className="flex items-start">
                          <CheckSquare className="h-4 w-4 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Important Dates */}
              {document.ai_important_dates && document.ai_important_dates.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Important Dates</h4>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="space-y-3">
                      {document.ai_important_dates.map((dateInfo, index) => (
                        <div key={index} className="flex items-start">
                          <Calendar className="h-4 w-4 text-purple-600 mt-0.5 mr-2 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{dateInfo.date}</p>
                            <p className="text-sm text-gray-600">{dateInfo.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Items */}
              {document.ai_action_items && document.ai_action_items.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Action Items</h4>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <ul className="space-y-2">
                      {document.ai_action_items.map((action, index) => (
                        <li key={index} className="flex items-start">
                          <CheckSquare className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-sm text-gray-700">{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Parties Involved */}
              {document.ai_parties_involved && document.ai_parties_involved.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Parties Involved</h4>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex flex-wrap gap-2">
                      {document.ai_parties_involved.map((party, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                        >
                          <Users className="h-3 w-3 mr-1" />
                          {party}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {!aiStatus?.ai_processed && !aiStatus?.ai_processing_error && (
            <div className="text-center py-8">
              <Brain className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">This document has not been processed with AI yet.</p>
              <p className="text-sm text-gray-400 mt-2">Click the "Process with AI" button above to analyze this document.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentAIInsights;